import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import {
  Avatar,
  Chip,
  Collapse,
  LinearProgress,
  MenuItem,
  Select,
} from "@mui/material";
import { toast } from "react-toastify";

import LoadingSpinner from "../../../../Components/Common/Spinner";
import AppModal from "../../../../Components/Common/AppModal";
import filterActiveStaff from "../../../../Components/Common/staffActive";
import StaffArchivedBadge from "../../../../Components/Common/StaffArchivedBadge";
import { getStaffListApi } from "../../../../helpers/fakebackend_helper";
import OnboardingTemplateStepsBoard from "../../../OnboardingTemplates/OnboardingTemplateStepsBoard";
import {
  listOnboardingForCustomer,
  getCompanyOnboarding,
  getOnboardingProgress,
  startOnboarding,
  setOnboardingStepStatus,
  updateOnboardingStep,
  listOnboardingTemplates,
  listStepStatuses,
  importTemplateIntoOnboarding,
  saveOnboardingAsTemplate,
  createOnboardingGroup,
  updateOnboardingGroup,
  deleteOnboardingGroup,
  createOnboardingStep,
  deleteOnboardingStep,
  reorderOnboardingSteps,
  listOnboardingNotes,
  createStepNote,
  deleteStepNote,
} from "./onboardingService";

// Current user + developer flag (localStorage authUser). In a company DB
// Staff.Id == User.Id, so User_Id is the acting person's staff id for "For me".
const authUserData = (() => {
  try {
    return JSON.parse(localStorage.getItem("authUser"))?.get_user_data || null;
  } catch {
    return null;
  }
})();
const CURRENT_USER_ID = authUserData?.User_Id ?? null;
const IS_DEVELOPER = String(authUserData?.User_FlagDeveloper ?? "0") === "1";

// A step is overdue when it has a due date in the past and isn't resolved.
const isOverdueStep = (step, isResolvedFn) => {
  if (isResolvedFn(step)) return false;
  if (!step.DueDate) return false;
  const due = new Date(String(step.DueDate).slice(0, 10));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return !isNaN(due.getTime()) && due < today;
};

// Customer onboarding checklist.
//
// Rendered from Settings > Super Admin > Onboarding (a customer is picked there
// and passed in as `customerId`). Previously a tab on the customer single page;
// the UI is unchanged, only its home moved.
//
// A template-driven checklist that walks a new customer through the same setup
// every time. Onboarding is really a structured but NON-LINEAR data-collection
// interview: staff jump between grouped categories, most items get parked as
// follow-ups ("waiting on the customer to send X"), each item captures an
// answer, and many point at the Settings / import screen where the answer is
// configured. So instead of a flat ordered tick-list we render:
//   - steps grouped into collapsible CATEGORY sections with a per-section tally,
//   - a multi-state STATUS per step (To do / In progress / Waiting on customer /
//     N/A / Done) rather than a bare checkbox,
//   - a follow-up OWNER + due date when a step is waiting,
//   - a captured VALUE (the answer) and free NOTES, both autosaved,
//   - a deep LINK that opens the relevant Settings / import screen in a new tab,
//   - a "follow-ups only" filter and an outstanding-follow-up count.
// Backed by CustomerOnboarding / CustomerOnboardingStep.

const ONBOARDING_STATUS_META = {
  "not-started": { label: "Not started", color: "default" },
  "in-progress": { label: "In progress", color: "warning" },
  complete: { label: "Complete", color: "success" },
};

// Per-step status now comes from the CustomerOnboardingStepStatus lookup
// (New / In Progress / Blocked / Completed / N/A), joined onto each step as
// CustomerOnboardingStepStatus_Id / _Slug / _Name / _Color / _FlagResolved.
// "Blocked" is the follow-up state (waiting on someone). `FlagResolved` marks
// the states (Completed, N/A) that count as done for the progress tally.
const FOLLOWUP_SLUG = "blocked";

// Fall back to the legacy FlagComplete for any step that predates the lookup.
const stepStatus = (step) =>
  step.CustomerOnboardingStepStatus_Slug ||
  (Number(step.FlagComplete) === 1 ? "completed" : "new");

const isResolved = (step) =>
  step.CustomerOnboardingStepStatus_FlagResolved != null
    ? Number(step.CustomerOnboardingStepStatus_FlagResolved) === 1
    : Number(step.FlagComplete) === 1;

const isFollowUp = (step) => stepStatus(step) === FOLLOWUP_SLUG;

// A step marked "not applicable" is greyed out and dropped from the progress
// maths (group tally + overall %), and doesn't block completion.
const isExcluded = (step) => Number(step.FlagExcluded) === 1;


const formatDateTime = (value) => {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  return isNaN(d.getTime()) ? value : d.toLocaleString();
};

// Convert a DB datetime ("YYYY-MM-DD HH:MM:SS") to the value a
// <input type="datetime-local"> expects ("YYYY-MM-DDTHH:MM"), and back.
const toDateTimeLocal = (value) =>
  value ? String(value).replace(" ", "T").slice(0, 16) : "";
const fromDateTimeLocal = (value) =>
  value ? `${value.replace("T", " ")}:00` : null;

// Initials from a full name for the staff avatar.
const initialsOf = (name) =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() || "")
    .join("") || "?";

// Deterministic brand-ish colour per name so avatars are stable + distinguishable.
const AVATAR_COLORS = [
  "#405189", "#0ab39c", "#f7b84b", "#f06548", "#299cdb", "#6559cc", "#f672a7",
];
const avatarColor = (name) => {
  const s = String(name || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
};

// Small round avatar with initials — used next to staff names (matches the
// avatar-beside-name convention used elsewhere in the app).
function StaffAvatar({ name, size = 22 }) {
  return (
    <Avatar
      sx={{
        width: size,
        height: size,
        fontSize: size * 0.42,
        bgcolor: avatarColor(name),
      }}
    >
      {initialsOf(name)}
    </Avatar>
  );
}

export default function CustomerOnboarding({
  customerId: customerIdProp,
  companyMode = false,
}) {
  // Company mode (Company Settings > Onboarding) loads the single company-level
  // onboarding via find-or-create — no customer. Otherwise this is the legacy
  // per-customer flow: a customer id comes from the prop or the route param.
  const params = useParams();
  const customerId = customerIdProp || params?.id;

  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [progress, setProgress] = useState(null); // full progress payload
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [statusOptions, setStatusOptions] = useState([]); // from the lookup
  const [busyStepId, setBusyStepId] = useState(null);
  const [drafts, setDrafts] = useState({}); // { [stepId]: { CapturedValue, Notes } }
  const [followUpsOnly, setFollowUpsOnly] = useState(false);
  const [staff, setStaff] = useState([]); // for the per-step assignee picker
  const [notes, setNotes] = useState([]); // per-step note history (whole onboarding)
  const [stepAccordion, setStepAccordion] = useState({}); // { [stepId]: true }

  const toggleStepOpen = (id) =>
    setStepAccordion((m) => ({ ...m, [id]: !m[id] }));
  const setGroupStepsOpen = (stepObjs, value) =>
    setStepAccordion((m) => {
      const next = { ...m };
      stepObjs.forEach((s) => {
        next[s.Id] = value;
      });
      return next;
    });

  // Filters: by assigned person, "for me", and overdue.
  const [filterStaffId, setFilterStaffId] = useState("");
  const [mineOnly, setMineOnly] = useState(false);
  const [overdueOnly, setOverdueOnly] = useState(false);

  // Import-template + save-as-template modals.
  const [importOpen, setImportOpen] = useState(false);
  const [importTemplateId, setImportTemplateId] = useState("");
  const [importing, setImporting] = useState(false);
  const [saveTplOpen, setSaveTplOpen] = useState(false);
  const [saveTplName, setSaveTplName] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);

  // Group/step edit modals (structural editing of the run).
  const [grpOpen, setGrpOpen] = useState(false);
  const [grpMode, setGrpMode] = useState("create");
  const [grpEditId, setGrpEditId] = useState(null);
  const [grpForm, setGrpForm] = useState({ Name: "", Description: "", Order: "" });
  const [grpSaving, setGrpSaving] = useState(false);
  const [stepOpen, setStepOpen] = useState(false);
  const [stepMode, setStepMode] = useState("create");
  const [stepEditId, setStepEditId] = useState(null);
  const [stepForm, setStepForm] = useState({
    Title: "",
    Description: "",
    CustomerOnboardingGroup_Id: "",
    Order: "",
    FlagRequired: 1,
    LinkUrl: "",
    LinkLabel: "",
  });
  const [stepSaving, setStepSaving] = useState(false);

  // Quick lookup of a status row by Id / Slug for the selectors and defaults.
  const statusById = useMemo(() => {
    const m = {};
    for (const s of statusOptions) m[String(s.Id)] = s;
    return m;
  }, [statusOptions]);
  const statusIdBySlug = useMemo(() => {
    const m = {};
    for (const s of statusOptions) m[s.Slug] = s.Id;
    return m;
  }, [statusOptions]);

  const steps = useMemo(
    () => progress?.customeronboardingstep_list ?? [],
    [progress]
  );
  const instanceGroups = useMemo(
    () => progress?.customeronboardinggroup_list ?? [],
    [progress]
  );

  // Note history grouped by step id.
  const notesByStep = useMemo(() => {
    const m = {};
    for (const n of notes) {
      (m[n.CustomerOnboardingStep_Id] =
        m[n.CustomerOnboardingStep_Id] || []).push(n);
    }
    return m;
  }, [notes]);
  const statusMeta =
    ONBOARDING_STATUS_META[progress?.onboarding_status] ||
    ONBOARDING_STATUS_META["not-started"];

  const hasOnboarding = !!progress?.customeronboarding_data?.Id;

  // Derived tallies. Excluded ("not applicable") steps are dropped from the
  // maths; progress is weighted on resolved (done + N/A) of what's left.
  const includedSteps = useMemo(
    () => steps.filter((s) => !isExcluded(s)),
    [steps]
  );
  const resolvedCount = useMemo(
    () => includedSteps.filter(isResolved).length,
    [includedSteps]
  );
  const followUpCount = useMemo(
    () => steps.filter(isFollowUp).length,
    [steps]
  );
  const overdueCount = useMemo(
    () => steps.filter((s) => !isExcluded(s) && isOverdueStep(s, isResolved)).length,
    [steps]
  );
  const anyFilterActive =
    followUpsOnly || mineOnly || overdueOnly || !!filterStaffId;
  const percent = includedSteps.length
    ? Math.round((resolvedCount / includedSteps.length) * 100)
    : 0;

  // A step is visible under the current filters. When a filter is active we
  // show only matching steps and drag is disabled (reordering a filtered subset
  // would be meaningless).
  const stepVisible = (step) => {
    if (followUpsOnly && !isFollowUp(step)) return false;
    if (filterStaffId && String(step.AssignedStaffId) !== String(filterStaffId))
      return false;
    if (mineOnly && String(step.AssignedStaffId) !== String(CURRENT_USER_ID))
      return false;
    if (overdueOnly && !isOverdueStep(step, isResolved)) return false;
    return true;
  };
  const visibleSteps = useMemo(
    () => steps.filter(stepVisible),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [steps, followUpsOnly, filterStaffId, mineOnly, overdueOnly]
  );
  // Steps fed to the board: the full set normally, the filtered set when filtering.
  const boardSteps = anyFilterActive ? visibleSteps : steps;

  // Load the onboarding + its progress. Company mode find-or-creates the single
  // company onboarding in one call; customer mode opens the most recent one.
  const loadOnboarding = async () => {
    setLoading(true);
    if (companyMode) {
      const prog = await getCompanyOnboarding();
      if (prog?.status === 200) {
        setProgress(prog);
      } else if (prog) {
        toast.error(prog.message || "Failed to load onboarding");
      }
      setLoading(false);
      return;
    }
    if (!customerId) {
      setLoading(false);
      return;
    }
    const res = await listOnboardingForCustomer(customerId);
    if (res?.status === 200) {
      const list = res.customeronboarding_list ?? [];
      if (list.length) {
        // forcustomer returns newest first; open the most recent onboarding.
        const latest = list[0];
        const prog = await getOnboardingProgress(latest.Id);
        if (prog?.status === 200) {
          setProgress(prog);
        } else if (prog) {
          toast.error(prog.message || "Failed to load onboarding progress");
        }
      } else {
        setProgress(null);
      }
    } else if (res) {
      toast.error(res.message || "Failed to load onboarding");
    }
    setLoading(false);
  };

  // The fixed status lookup (New / In Progress / Blocked / Completed / N/A).
  const loadStatuses = async () => {
    const res = await listStepStatuses();
    if (res?.status === 200) {
      setStatusOptions(res.customeronboardingstepstatus_list ?? []);
    }
  };

  // Templates for the "start" picker.
  const loadTemplates = async () => {
    const res = await listOnboardingTemplates();
    if (res?.status === 200) {
      const list = (res.onboardingchecklisttemplate_list ?? []).filter(
        (t) => Number(t.FlagActive) === 1
      );
      setTemplates(list);
      const def = list.find((t) => Number(t.FlagDefault) === 1);
      setTemplateId(def ? String(def.Id) : list[0] ? String(list[0].Id) : "");
    }
  };

  useEffect(() => {
    loadOnboarding();
    loadTemplates();
    loadStatuses();
    getStaffListApi()
      .then((res) => setStaff(res?.staff_list || []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId, companyMode]);

  // Load the per-step note history whenever the onboarding changes.
  useEffect(() => {
    loadNotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progress?.customeronboarding_data?.Id]);

  // Active staff for the assignee dropdown, keeping any already-assigned leaver
  // in the options (flagged with an archived badge) so the current selection
  // still renders. See the active-staff picker convention in staffActive.js.
  const staffOptionsFor = (step) =>
    filterActiveStaff(staff, [step.AssignedStaffId]);

  const refreshProgress = async () => {
    const id = progress?.customeronboarding_data?.Id;
    if (!id) return;
    const prog = await getOnboardingProgress(id);
    if (prog?.status === 200) setProgress(prog);
  };

  const handleStart = async () => {
    setStarting(true);
    const res = await startOnboarding(
      customerId,
      templateId ? Number(templateId) : undefined
    );
    setStarting(false);
    if (res?.status === 200) {
      toast.success(res.message || "Onboarding started");
      const prog = await getOnboardingProgress(res.CustomerOnboarding_Id);
      if (prog?.status === 200) setProgress(prog);
    } else if (res) {
      toast.error(res.message || "Could not start onboarding");
    }
  };

  // Import a template's groups + steps into the current onboarding (append).
  const handleImportTemplate = async () => {
    const onboardingId = progress?.customeronboarding_data?.Id;
    if (!onboardingId || !importTemplateId) return;
    setImporting(true);
    const res = await importTemplateIntoOnboarding(
      onboardingId,
      Number(importTemplateId)
    );
    setImporting(false);
    if (res?.status === 200) {
      toast.success(res.message || "Template imported");
      setImportOpen(false);
      await refreshProgress();
    } else if (res) {
      toast.error(res.message || "Could not import template");
    }
  };

  // Save the current onboarding's groups + steps back as a new master template.
  const handleSaveAsTemplate = async () => {
    const onboardingId = progress?.customeronboarding_data?.Id;
    if (!onboardingId || !saveTplName.trim()) return;
    setSavingTpl(true);
    const res = await saveOnboardingAsTemplate(onboardingId, {
      Name: saveTplName.trim(),
    });
    setSavingTpl(false);
    if (res?.status === 200) {
      toast.success(res.message || "Saved as template");
      setSaveTplOpen(false);
      setSaveTplName("");
      loadTemplates();
    } else if (res) {
      toast.error(res.message || "Could not save as template");
    }
  };

  // ── Structural editing of the running onboarding (edit-board mode) ──
  const asInt = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);

  const handleReorderInstance = async (rows) => {
    if (!rows.length) return;
    const res = await reorderOnboardingSteps(rows);
    if (res && res.status !== 200) {
      toast.error(res.message || "Could not save the new order");
    }
    await refreshProgress();
  };

  const openCreateGroup = () => {
    setGrpMode("create");
    setGrpEditId(null);
    setGrpForm({
      Name: "",
      Description: "",
      Order: String((instanceGroups.length + 1) * 10),
    });
    setGrpOpen(true);
  };
  const openEditGroup = (group) => {
    setGrpMode("edit");
    setGrpEditId(group.Id);
    setGrpForm({
      Name: group.Name ?? "",
      Description: group.Description ?? "",
      Order: group.Order ?? "",
    });
    setGrpOpen(true);
  };
  const handleSaveGroup = async () => {
    const onboardingId = progress?.customeronboarding_data?.Id;
    if (!grpForm.Name.trim() || !onboardingId) return;
    setGrpSaving(true);
    const payload = {
      Name: grpForm.Name.trim(),
      Description: grpForm.Description,
      Order: asInt(grpForm.Order),
    };
    const res =
      grpMode === "create"
        ? await createOnboardingGroup({
            CustomerOnboarding_Id: onboardingId,
            ...payload,
          })
        : await updateOnboardingGroup(grpEditId, payload);
    setGrpSaving(false);
    if (res?.status === 200) {
      toast.success(grpMode === "create" ? "Group added" : "Group updated");
      setGrpOpen(false);
      await refreshProgress();
    } else if (res) {
      toast.error(res.message || "Could not save group");
    }
  };
  const handleDeleteGroup = async (group) => {
    if (
      !window.confirm(
        `Delete group "${group.Name}"? Its steps are kept but become ungrouped.`
      )
    )
      return;
    const res = await deleteOnboardingGroup(group.Id);
    if (res?.status === 200) {
      toast.success("Group deleted");
      await refreshProgress();
    } else if (res) {
      toast.error(res.message || "Could not delete group");
    }
  };

  const openCreateStep = (groupId = "") => {
    setStepMode("create");
    setStepEditId(null);
    setStepForm({
      Title: "",
      Description: "",
      CustomerOnboardingGroup_Id: groupId ? String(groupId) : "",
      Order: String((steps.length + 1) * 10),
      FlagRequired: 1,
      LinkUrl: "",
      LinkLabel: "",
    });
    setStepOpen(true);
  };
  const openEditStep = (step) => {
    setStepMode("edit");
    setStepEditId(step.Id);
    setStepForm({
      Title: step.Title ?? "",
      Description: step.Description ?? "",
      CustomerOnboardingGroup_Id: step.CustomerOnboardingGroup_Id
        ? String(step.CustomerOnboardingGroup_Id)
        : "",
      Order: step.Order ?? "",
      FlagRequired: Number(step.FlagRequired) === 1 ? 1 : 0,
      LinkUrl: step.LinkUrl ?? "",
      LinkLabel: step.LinkLabel ?? "",
    });
    setStepOpen(true);
  };
  const handleSaveStep = async () => {
    const onboardingId = progress?.customeronboarding_data?.Id;
    if (!stepForm.Title.trim() || !onboardingId) return;
    setStepSaving(true);
    const payload = {
      Title: stepForm.Title.trim(),
      Description: stepForm.Description,
      CustomerOnboardingGroup_Id: asInt(stepForm.CustomerOnboardingGroup_Id),
      Order: asInt(stepForm.Order),
      FlagRequired: stepForm.FlagRequired ? 1 : 0,
      LinkUrl: stepForm.LinkUrl.trim(),
      LinkLabel: stepForm.LinkLabel.trim(),
    };
    const res =
      stepMode === "create"
        ? await createOnboardingStep({
            CustomerOnboarding_Id: onboardingId,
            ...payload,
          })
        : await updateOnboardingStep(stepEditId, payload);
    setStepSaving(false);
    if (res?.status === 200) {
      toast.success(stepMode === "create" ? "Step added" : "Step updated");
      setStepOpen(false);
      await refreshProgress();
    } else if (res) {
      toast.error(res.message || "Could not save step");
    }
  };
  const handleDeleteInstanceStep = async (step) => {
    if (!window.confirm(`Remove step "${step.Title}"?`)) return;
    const res = await deleteOnboardingStep(step.Id);
    if (res?.status === 200) {
      toast.success("Step removed");
      await refreshProgress();
    } else if (res) {
      toast.error(res.message || "Could not remove step");
    }
  };

  // Status change persists immediately and re-fetches so the parent status /
  // percentage / follow-up count refresh.
  const handleStatusChange = async (step, nextStatusId) => {
    setBusyStepId(step.Id);
    const draft = drafts[step.Id] || {};
    const nextSlug = statusById[String(nextStatusId)]?.Slug;
    const res = await setOnboardingStepStatus(step.Id, nextStatusId, {
      // Carry any unsaved captured value / note through with the status change.
      ...(draft.CapturedValue !== undefined
        ? { CapturedValue: draft.CapturedValue }
        : {}),
      ...(draft.Notes !== undefined ? { Notes: draft.Notes } : {}),
      // Default the follow-up owner to the customer when a step becomes blocked.
      ...(nextSlug === FOLLOWUP_SLUG && !step.FollowUpOwner
        ? { FollowUpOwner: "customer" }
        : {}),
    });
    if (res?.status === 200) {
      await refreshProgress();
    } else if (res) {
      toast.error(res.message || "Could not update status");
    }
    setBusyStepId(null);
  };

  // Owner / due date are lightweight follow-up metadata — save silently and
  // patch local state so the follow-up view stays in sync without a re-fetch.
  const patchStepLocally = (stepId, fields) =>
    setProgress((prev) =>
      prev
        ? {
            ...prev,
            customeronboardingstep_list: prev.customeronboardingstep_list.map(
              (s) => (s.Id === stepId ? { ...s, ...fields } : s)
            ),
          }
        : prev
    );

  const handleOwnerChange = async (step, owner) => {
    patchStepLocally(step.Id, { FollowUpOwner: owner });
    const res = await updateOnboardingStep(step.Id, { FollowUpOwner: owner });
    if (res && res.status !== 200) {
      toast.error(res.message || "Could not save follow-up owner");
    }
  };

  const handleDueChange = async (step, due) => {
    patchStepLocally(step.Id, { FollowUpDueDate: due || null });
    const res = await updateOnboardingStep(step.Id, {
      FollowUpDueDate: due || null,
    });
    if (res && res.status !== 200) {
      toast.error(res.message || "Could not save due date");
    }
  };

  // Assignee + the step's own due date — lightweight metadata, saved silently
  // and patched locally so the row updates without a full re-fetch.
  const handleAssigneeChange = async (step, staffId) => {
    const id = staffId ? Number(staffId) : null;
    const picked = staff.find((s) => String(s.Id) === String(staffId));
    patchStepLocally(step.Id, {
      AssignedStaffId: id,
      AssignedStaff_FullName: picked ? picked.FullName : null,
    });
    const res = await updateOnboardingStep(step.Id, { AssignedStaffId: id });
    if (res && res.status !== 200) {
      toast.error(res.message || "Could not save assignee");
    }
  };

  const handleStepDueDateChange = async (step, due) => {
    patchStepLocally(step.Id, { DueDate: due || null });
    const res = await updateOnboardingStep(step.Id, { DueDate: due || null });
    if (res && res.status !== 200) {
      toast.error(res.message || "Could not save due date");
    }
  };

  // Edit the completed date/time directly (e.g. back-date a completion).
  const handleCompletedDateChange = async (step, localValue) => {
    const val = fromDateTimeLocal(localValue);
    patchStepLocally(step.Id, { CompletedDateTime: val });
    const res = await updateOnboardingStep(step.Id, { CompletedDateTime: val });
    if (res && res.status !== 200) {
      toast.error(res.message || "Could not save completed date");
    }
  };

  // Toggle a step between Required and Optional (affects the completion rollup).
  const handleToggleRequired = async (step) => {
    const next = Number(step.FlagRequired) === 1 ? 0 : 1;
    patchStepLocally(step.Id, { FlagRequired: next });
    const res = await updateOnboardingStep(step.Id, { FlagRequired: next });
    if (res?.status === 200) {
      refreshProgress();
    } else if (res) {
      toast.error(res.message || "Could not update step");
    }
  };

  // Mark a step "not applicable" (excluded) or restore it. Patches locally so the
  // bar/tally re-weight instantly, then re-fetches to re-roll parent completion.
  const handleToggleExcluded = async (step) => {
    const next = isExcluded(step) ? 0 : 1;
    patchStepLocally(step.Id, { FlagExcluded: next });
    const res = await updateOnboardingStep(step.Id, { FlagExcluded: next });
    if (res?.status === 200) {
      refreshProgress();
    } else if (res) {
      toast.error(res.message || "Could not update step");
    }
  };

  // ── Per-step note history ──
  const loadNotes = async () => {
    const id = progress?.customeronboarding_data?.Id;
    if (!id) {
      setNotes([]);
      return;
    }
    const res = await listOnboardingNotes(id);
    if (res?.status === 200) {
      setNotes(res.customeronboardingstepnote_list ?? []);
    }
  };

  const handleAddNote = async (step, text) => {
    if (!text || !text.trim()) return false;
    const res = await createStepNote(step.Id, text.trim());
    if (res?.status === 200) {
      await loadNotes();
      return true;
    }
    if (res) toast.error(res.message || "Could not add note");
    return false;
  };

  const handleDeleteNote = async (noteId) => {
    const res = await deleteStepNote(noteId);
    if (res?.status === 200) {
      await loadNotes();
    } else if (res) {
      toast.error(res.message || "Could not delete note");
    }
  };

  // Captured value + notes autosave on blur when the draft differs from server.
  const handleFieldSave = async (step, field) => {
    const draft = drafts[step.Id];
    if (!draft || draft[field] === undefined) return;
    const current = step[field] ?? "";
    if (draft[field] === current) return;
    const res = await updateOnboardingStep(step.Id, { [field]: draft[field] });
    if (res?.status === 200) {
      patchStepLocally(step.Id, { [field]: draft[field] });
    } else if (res) {
      toast.error(res.message || "Could not save");
    }
  };

  const setDraft = (stepId, field, value) =>
    setDrafts((d) => ({
      ...d,
      [stepId]: { ...d[stepId], [field]: value },
    }));

  const draftValue = (step, field) =>
    drafts[step.Id]?.[field] ?? step[field] ?? "";

  // Right-side content of each group header on the board: waiting chip + a
  // progress bar (excluded steps ignored). Mirrors the overall progress bar.
  const renderGroupMeta = (group, stepObjs) => {
    const included = stepObjs.filter((s) => !isExcluded(s));
    const total = included.length;
    const resolved = included.filter(isResolved).length;
    const waiting = stepObjs.filter(isFollowUp).length;
    const pct = total ? Math.round((resolved / total) * 100) : 0;
    const allStepsOpen =
      stepObjs.length > 0 && stepObjs.every((s) => stepAccordion[s.Id]);
    return (
      <span className="d-flex align-items-center gap-2">
        {stepObjs.length > 0 ? (
          <Button
            color="light"
            size="sm"
            title={allStepsOpen ? "Collapse all steps" : "Expand all steps"}
            onClick={() => setGroupStepsOpen(stepObjs, !allStepsOpen)}
          >
            <i
              className={`align-bottom ${
                allStepsOpen ? "ri-contract-up-down-line" : "ri-expand-up-down-line"
              }`}
            />
          </Button>
        ) : null}
        {waiting > 0 ? (
          <Chip
            label={`${waiting} waiting`}
            color="warning"
            size="small"
            variant="outlined"
          />
        ) : null}
        <LinearProgress
          variant="determinate"
          value={pct}
          color={pct === 100 ? "success" : "primary"}
          sx={{
            width: 70,
            height: 6,
            borderRadius: 3,
            display: { xs: "none", sm: "block" },
          }}
        />
        <span
          className="text-white small fw-semibold"
          style={{ minWidth: 34, textAlign: "right" }}
        >
          {pct}%
        </span>
        <span className="text-white-50 small">
          {resolved}/{total}
        </span>
      </span>
    );
  };

  // Rich onboarding row rendered inside the board's sortable wrapper. The board
  // hands us the drag handle + move-to menu via ctx.
  const renderRichStep = (step, ctx) => (
    <OnboardingStepRow
      step={step}
      statusOptions={statusOptions}
      statusIdBySlug={statusIdBySlug}
      staffOptions={staffOptionsFor(step)}
      notes={notesByStep[step.Id] || []}
      busy={busyStepId === step.Id}
      onStatusChange={handleStatusChange}
      onOwnerChange={handleOwnerChange}
      onDueChange={handleDueChange}
      onAssigneeChange={handleAssigneeChange}
      onStepDueDateChange={handleStepDueDateChange}
      onCompletedDateChange={handleCompletedDateChange}
      onToggleExcluded={handleToggleExcluded}
      onToggleRequired={handleToggleRequired}
      onAddNote={handleAddNote}
      onDeleteNote={handleDeleteNote}
      onFieldSave={handleFieldSave}
      setDraft={setDraft}
      draftValue={draftValue}
      dragHandleProps={ctx.handleProps}
      moveTargets={ctx.moveTargets}
      currentKey={ctx.currentKey}
      onMove={ctx.onMove}
      onEditStructure={openEditStep}
      onDeleteStructure={handleDeleteInstanceStep}
      open={!!stepAccordion[step.Id]}
      onToggleOpen={() => toggleStepOpen(step.Id)}
    />
  );

  if (loading) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <LoadingSpinner />
        </CardBody>
      </Card>
    );
  }

  // ── Company mode should always have an onboarding (find-or-create). If the
  //    fetch failed, show a retry rather than the customer "start" picker. ──
  if (!hasOnboarding && companyMode) {
    return (
      <Card>
        <CardBody className="text-center py-5">
          <p className="text-muted mb-3">
            Couldn't load the onboarding checklist.
          </p>
          <Button color="primary" onClick={loadOnboarding}>
            Retry
          </Button>
        </CardBody>
      </Card>
    );
  }

  // ── No onboarding yet: offer to start one (customer mode) ──
  if (!hasOnboarding) {
    return (
      <Card>
        <CardBody>
          <h5 className="mb-2">Customer onboarding</h5>
          <p className="text-muted mb-4">
            This customer hasn't been onboarded yet. Start the onboarding
            checklist to work through the setup — jump between sections in any
            order, capture answers, and park anything you're waiting on the
            customer for.
          </p>
          <Row className="g-2 align-items-end">
            <Col md={6} lg={4}>
              <label className="form-label mb-1">Checklist template</label>
              <Select
                fullWidth
                size="small"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                displayEmpty
              >
                {templates.length === 0 && (
                  <MenuItem value="">Default template</MenuItem>
                )}
                {templates.map((t) => (
                  <MenuItem key={t.Id} value={String(t.Id)}>
                    {t.Name}
                    {Number(t.FlagDefault) === 1 ? " (default)" : ""}
                  </MenuItem>
                ))}
              </Select>
            </Col>
            <Col md="auto">
              <Button color="primary" onClick={handleStart} disabled={starting}>
                {starting ? "Starting…" : "Start onboarding"}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  }

  // ── Active / complete onboarding: the grouped checklist ──
  return (
    <Card>
      <CardBody>
        {/* Header: template, dates, derived status */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
          <div>
            <h5 className="mb-1">
              {progress.customeronboarding_data.OnboardingChecklistTemplate_Name ||
                "Customer onboarding"}
            </h5>
            <div className="text-muted small">
              Started{" "}
              {formatDateTime(progress.customeronboarding_data.StartedDateTime)}
              {progress.customeronboarding_data.CompletedDateTime
                ? ` · Completed ${formatDateTime(
                    progress.customeronboarding_data.CompletedDateTime
                  )}`
                : ""}
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Chip
              label={statusMeta.label}
              color={statusMeta.color}
              size="small"
              variant={statusMeta.color === "default" ? "outlined" : "filled"}
            />
            <Button
              color="soft-primary"
              size="sm"
              onClick={() => {
                const def = templates.find((t) => Number(t.FlagDefault) === 1);
                setImportTemplateId(
                  def ? String(def.Id) : templates[0] ? String(templates[0].Id) : ""
                );
                setImportOpen(true);
              }}
            >
              <i className="ri-download-2-line align-bottom me-1" />
              Import template
            </Button>
            {IS_DEVELOPER ? (
              <Button
                color="soft-secondary"
                size="sm"
                onClick={() => {
                  setSaveTplName(
                    progress.customeronboarding_data
                      .OnboardingChecklistTemplate_Name
                      ? `Copy of ${progress.customeronboarding_data.OnboardingChecklistTemplate_Name}`
                      : "Onboarding template"
                  );
                  setSaveTplOpen(true);
                }}
              >
                <i className="ri-save-3-line align-bottom me-1" />
                Save as template
              </Button>
            ) : null}
          </div>
        </div>

        {/* Progress + follow-up controls */}
        <div className="mb-3">
          <div className="d-flex justify-content-between small text-muted mb-1">
            <span>{`${resolvedCount} of ${includedSteps.length} steps complete`}</span>
            <span>{percent}%</span>
          </div>
          <LinearProgress
            variant="determinate"
            value={percent}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {/* Person filter */}
          <Select
            size="small"
            displayEmpty
            value={filterStaffId}
            onChange={(e) => setFilterStaffId(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">
              <span className="text-muted">All assignees</span>
            </MenuItem>
            {filterActiveStaff(staff, [filterStaffId]).map((s) => (
              <MenuItem key={s.Id} value={String(s.Id)}>
                {s.FullName}
              </MenuItem>
            ))}
          </Select>

          {/* For me */}
          {CURRENT_USER_ID ? (
            <Chip
              icon={<i className="ri-user-line" style={{ fontSize: 16 }} />}
              label="For me"
              color="primary"
              size="small"
              variant={mineOnly ? "filled" : "outlined"}
              onClick={() => setMineOnly((v) => !v)}
              sx={{ cursor: "pointer" }}
            />
          ) : null}

          {/* Overdue */}
          <Chip
            icon={<i className="ri-alarm-warning-line" style={{ fontSize: 16 }} />}
            label={overdueCount > 0 ? `${overdueCount} overdue` : "Overdue"}
            color="error"
            size="small"
            variant={overdueOnly ? "filled" : "outlined"}
            onClick={() => setOverdueOnly((v) => !v)}
            sx={{ cursor: "pointer" }}
          />

          {/* Follow-ups */}
          {followUpCount > 0 ? (
            <Chip
              icon={<i className="ri-time-line" style={{ fontSize: 16 }} />}
              label={`${followUpCount} awaiting customer`}
              color="warning"
              size="small"
              variant={followUpsOnly ? "filled" : "outlined"}
              onClick={() => setFollowUpsOnly((v) => !v)}
              sx={{ cursor: "pointer" }}
            />
          ) : null}

          {anyFilterActive ? (
            <Button
              color="link"
              size="sm"
              className="p-0"
              onClick={() => {
                setFollowUpsOnly(false);
                setMineOnly(false);
                setOverdueOnly(false);
                setFilterStaffId("");
              }}
            >
              Clear filters
            </Button>
          ) : null}

          <span className="d-flex align-items-center gap-2 ms-auto">
            <Button color="soft-primary" size="sm" onClick={openCreateGroup}>
              <i className="ri-folder-add-line align-bottom me-1" /> Add group
            </Button>
            <Button color="primary" size="sm" onClick={() => openCreateStep()}>
              <i className="ri-add-line align-bottom me-1" /> Add step
            </Button>
          </span>
        </div>

        {/* Single unified board: work the steps AND reorder / regroup / edit */}
        {instanceGroups.length === 0 && steps.length === 0 ? (
          <div className="text-muted">
            This onboarding has no steps. Import a template to populate the
            checklist, or add a group and steps.
          </div>
        ) : (
          <>
            <OnboardingTemplateStepsBoard
              groups={instanceGroups}
              steps={boardSteps}
              groupIdField="CustomerOnboardingGroup_Id"
              disabled={anyFilterActive}
              onCreateStep={openCreateStep}
              onEditStep={openEditStep}
              onDeleteStep={handleDeleteInstanceStep}
              onEditGroup={openEditGroup}
              onDeleteGroup={handleDeleteGroup}
              onPersist={handleReorderInstance}
              renderGroupMeta={renderGroupMeta}
              renderStep={renderRichStep}
            />
            {anyFilterActive ? (
              <div className="text-muted small mt-2">
                Filtered view — clear filters to drag/reorder steps.
              </div>
            ) : null}
          </>
        )}

        {/* Import template modal */}
        <AppModal
          open={importOpen}
          onClose={() => setImportOpen(false)}
          title="Import a template"
          size="sm"
          loading={importing}
          onSubmit={handleImportTemplate}
          submitLabel="Import"
          submitDisabled={!importTemplateId}
        >
          <p className="text-muted small">
            Adds the chosen template's groups and steps to this onboarding. You
            can then reorder, edit or remove them without affecting the template.
          </p>
          <label className="form-label mb-1">Template</label>
          <Select
            fullWidth
            size="small"
            value={importTemplateId}
            onChange={(e) => setImportTemplateId(e.target.value)}
            displayEmpty
          >
            {templates.length === 0 && (
              <MenuItem value="">No templates available</MenuItem>
            )}
            {templates.map((t) => (
              <MenuItem key={t.Id} value={String(t.Id)}>
                {t.Name}
                {Number(t.FlagDefault) === 1 ? " (default)" : ""}
              </MenuItem>
            ))}
          </Select>
        </AppModal>

        {/* Save as template modal (developer-only) */}
        <AppModal
          open={saveTplOpen}
          onClose={() => setSaveTplOpen(false)}
          title="Save as template"
          size="sm"
          loading={savingTpl}
          onSubmit={handleSaveAsTemplate}
          submitLabel="Save template"
          submitDisabled={!saveTplName.trim()}
        >
          <p className="text-muted small">
            Saves this onboarding's current groups and steps back into the
            template library as a new reusable template.
          </p>
          <label className="form-label mb-1">Template name</label>
          <Input
            type="text"
            autoFocus
            value={saveTplName}
            onChange={(e) => setSaveTplName(e.target.value)}
            placeholder="e.g. Field service onboarding"
          />
        </AppModal>

        {/* Group create/edit modal (edit-board mode) */}
        <AppModal
          open={grpOpen}
          onClose={() => setGrpOpen(false)}
          title={grpMode === "create" ? "Add group" : "Edit group"}
          size="sm"
          loading={grpSaving}
          onSubmit={handleSaveGroup}
          submitLabel={grpMode === "create" ? "Add" : "Save"}
          submitDisabled={!grpForm.Name.trim()}
        >
          <div className="mb-3">
            <label className="form-label mb-1">Name</label>
            <Input
              type="text"
              autoFocus
              value={grpForm.Name}
              onChange={(e) => setGrpForm({ ...grpForm, Name: e.target.value })}
              placeholder="e.g. Setup clock in/out"
            />
          </div>
          <div className="mb-3">
            <label className="form-label mb-1">Description</label>
            <Input
              type="textarea"
              rows={2}
              value={grpForm.Description}
              onChange={(e) =>
                setGrpForm({ ...grpForm, Description: e.target.value })
              }
            />
          </div>
          <Row>
            <Col xs={6}>
              <label className="form-label mb-1">Order</label>
              <Input
                type="number"
                value={grpForm.Order}
                onChange={(e) => setGrpForm({ ...grpForm, Order: e.target.value })}
              />
            </Col>
          </Row>
        </AppModal>

        {/* Step create/edit modal (edit-board mode) */}
        <AppModal
          open={stepOpen}
          onClose={() => setStepOpen(false)}
          title={stepMode === "create" ? "Add step" : "Edit step"}
          size="sm"
          loading={stepSaving}
          onSubmit={handleSaveStep}
          submitLabel={stepMode === "create" ? "Add" : "Save"}
          submitDisabled={!stepForm.Title.trim()}
        >
          <div className="mb-3">
            <label className="form-label mb-1">Title</label>
            <Input
              type="text"
              autoFocus
              value={stepForm.Title}
              onChange={(e) => setStepForm({ ...stepForm, Title: e.target.value })}
              placeholder="e.g. Load staff data"
            />
          </div>
          <div className="mb-3">
            <label className="form-label mb-1">Description</label>
            <Input
              type="textarea"
              rows={2}
              value={stepForm.Description}
              onChange={(e) =>
                setStepForm({ ...stepForm, Description: e.target.value })
              }
            />
          </div>
          <Row>
            <Col xs={8}>
              <label className="form-label mb-1">Group</label>
              <Input
                type="select"
                value={stepForm.CustomerOnboardingGroup_Id}
                onChange={(e) =>
                  setStepForm({
                    ...stepForm,
                    CustomerOnboardingGroup_Id: e.target.value,
                  })
                }
              >
                <option value="">Ungrouped</option>
                {instanceGroups.map((g) => (
                  <option key={g.Id} value={String(g.Id)}>
                    {g.Name}
                  </option>
                ))}
              </Input>
            </Col>
            <Col xs={4}>
              <label className="form-label mb-1">Order</label>
              <Input
                type="number"
                value={stepForm.Order}
                onChange={(e) =>
                  setStepForm({ ...stepForm, Order: e.target.value })
                }
              />
            </Col>
          </Row>
          <Row className="mt-3">
            <Col xs={7}>
              <label className="form-label mb-1">Settings / import link</label>
              <Input
                type="text"
                placeholder="e.g. /settings/1/41"
                value={stepForm.LinkUrl}
                onChange={(e) =>
                  setStepForm({ ...stepForm, LinkUrl: e.target.value })
                }
              />
            </Col>
            <Col xs={5}>
              <label className="form-label mb-1">Link label</label>
              <Input
                type="text"
                placeholder="e.g. Attachment settings"
                value={stepForm.LinkLabel}
                onChange={(e) =>
                  setStepForm({ ...stepForm, LinkLabel: e.target.value })
                }
              />
            </Col>
          </Row>
          <div className="form-check mt-3">
            <Input
              type="checkbox"
              id="onb-step-required"
              checked={!!stepForm.FlagRequired}
              onChange={(e) =>
                setStepForm({
                  ...stepForm,
                  FlagRequired: e.target.checked ? 1 : 0,
                })
              }
            />
            <label htmlFor="onb-step-required" className="form-check-label">
              Required
            </label>
          </div>
        </AppModal>
      </CardBody>
    </Card>
  );
}

// Colour-tinted status "pill" dropdown, styled like the app's status chips.
function StatusPillSelect({ step, statusOptions, statusIdBySlug, busy, onChange }) {
  const status = stepStatus(step);
  const currentStatusId =
    step.CustomerOnboardingStepStatus_Id ?? statusIdBySlug[status] ?? "";
  const current = statusOptions.find(
    (o) => String(o.Id) === String(currentStatusId)
  );
  const color = current?.Color || "#adb5bd";

  return (
    <Select
      size="small"
      value={statusOptions.length ? String(currentStatusId) : ""}
      disabled={busy || !statusOptions.length}
      onChange={(e) => onChange(step, Number(e.target.value))}
      renderValue={() => (
        <span className="d-inline-flex align-items-center gap-2">
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: color,
              display: "inline-block",
            }}
          />
          <span style={{ fontWeight: 600, color }}>{current?.Name || "—"}</span>
        </span>
      )}
      sx={{
        minWidth: 150,
        borderRadius: 999,
        backgroundColor: `${color}14`,
        "& .MuiOutlinedInput-notchedOutline": { borderColor: `${color}66` },
        "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: color },
        "& .MuiSelect-select": { py: 0.5 },
      }}
    >
      {statusOptions.map((o) => (
        <MenuItem key={o.Id} value={String(o.Id)}>
          <span className="d-inline-flex align-items-center gap-2">
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: o.Color || "#adb5bd",
                display: "inline-block",
              }}
            />
            {o.Name}
          </span>
        </MenuItem>
      ))}
    </Select>
  );
}

// Chronological note history for a step: each note stamped with author + time,
// plus an inline add box.
function NotesThread({ step, notes, onAddNote, onDeleteNote }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!draft.trim()) return;
    setSaving(true);
    const ok = await onAddNote(step, draft);
    setSaving(false);
    if (ok) setDraft("");
  };

  return (
    <div className="mt-3">
      <label className="form-label mb-1 small text-muted d-flex align-items-center gap-1">
        <i className="ri-chat-history-line" /> Notes
        {notes.length ? (
          <span className="text-muted">({notes.length})</span>
        ) : null}
      </label>

      {notes.length ? (
        <div className="vstack gap-2 mb-2">
          {notes.map((n) => (
            <div key={n.Id} className="d-flex align-items-start gap-2">
              <StaffAvatar name={n.CreatedStaff_FullName} size={26} />
              <div
                className="flex-grow-1 rounded px-2 py-1"
                style={{ backgroundColor: "var(--vz-light, #f3f6f9)" }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <span className="small fw-medium">
                    {n.CreatedStaff_FullName || "Unknown"}
                  </span>
                  <span className="d-flex align-items-center gap-2">
                    <span className="text-muted" style={{ fontSize: 11 }}>
                      {formatDateTime(n.CreatedDateTime)}
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link text-muted p-0"
                      title="Delete note"
                      onClick={() => onDeleteNote(n.Id)}
                    >
                      <i className="ri-close-line" />
                    </button>
                  </span>
                </div>
                <div
                  className="small"
                  style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
                >
                  {n.Note}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : null}

      <div className="d-flex align-items-start gap-2">
        <Input
          type="textarea"
          rows={1}
          bsSize="sm"
          placeholder="Add a note to the history…"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") submit();
          }}
        />
        <Button
          color="primary"
          size="sm"
          disabled={saving || !draft.trim()}
          onClick={submit}
        >
          <i className="ri-send-plane-2-line align-bottom" />
        </Button>
      </div>
    </div>
  );
}

// A single onboarding step: status pill, assignee, due/completed dates, captured
// value and its note history.
function OnboardingStepRow({
  step,
  statusOptions,
  statusIdBySlug,
  staffOptions,
  notes,
  busy,
  onStatusChange,
  onOwnerChange,
  onDueChange,
  onAssigneeChange,
  onStepDueDateChange,
  onCompletedDateChange,
  onToggleExcluded,
  onToggleRequired,
  onAddNote,
  onDeleteNote,
  onFieldSave,
  setDraft,
  draftValue,
  dragHandleProps,
  moveTargets,
  currentKey,
  onMove,
  onEditStructure,
  onDeleteStructure,
  open,
  onToggleOpen,
}) {
  const status = stepStatus(step);
  const resolved = isResolved(step);
  const excluded = isExcluded(step);
  const isRequired = Number(step.FlagRequired) === 1;
  const waiting = status === FOLLOWUP_SLUG;
  const assigned = staffOptions.find(
    (s) => String(s.Id) === String(step.AssignedStaffId)
  );
  // The "Completed" status from the lookup, for the Mark Complete button.
  const completedStatus = statusOptions.find((o) => o.Slug === "completed");
  const isComplete = status === "completed";
  // Each step is its own accordion — collapsed by default. Open state is
  // controlled by the parent so a group can expand/collapse all its steps.
  const noteCount = notes.length;

  // "Move to group" modal: groups the step can be moved to (excludes current).
  const otherGroups = (moveTargets || []).filter((t) => t.key !== currentKey);
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState("");

  return (
    <div
      className="p-3 rounded"
      style={{
        border: "1px solid var(--vz-border-color, #e9ebec)",
        backgroundColor: excluded
          ? "var(--vz-light, #f3f6f9)"
          : resolved
          ? "var(--vz-light, #f8f9fa)"
          : "#fff",
        opacity: excluded ? 0.6 : 1,
      }}
    >
      {/* Header: grip + title + badges, with actions on the right */}
      <div className="d-flex align-items-start justify-content-between gap-2">
        <div className="d-flex align-items-start gap-2 flex-grow-1">
          {dragHandleProps ? (
            <span
              {...dragHandleProps}
              className="flex-shrink-0 text-secondary d-inline-flex align-items-center mt-1"
              title="Drag to reorder within the group, or onto another group"
            >
              <i className="ri-more-2-fill" style={{ fontSize: 18 }} />
              <i
                className="ri-more-2-fill"
                style={{ fontSize: 18, marginLeft: -8 }}
              />
            </span>
          ) : null}
          <button
            type="button"
            className="p-0 mt-1 flex-shrink-0 text-muted border-0 bg-transparent text-decoration-none"
            title={open ? "Collapse" : "Expand"}
            onClick={onToggleOpen}
          >
            <i
              className={open ? "ri-arrow-down-s-line" : "ri-arrow-right-s-line"}
              style={{ fontSize: 20 }}
            />
          </button>
          <div
            className="flex-grow-1"
            role="button"
            onClick={onToggleOpen}
            style={{ cursor: "pointer" }}
          >
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span
                style={{
                  fontWeight: 600,
                  textDecoration:
                    resolved || excluded ? "line-through" : "none",
                  opacity: resolved ? 0.7 : 1,
                }}
              >
                {step.Title}
              </span>
              {excluded ? (
                <Badge color="secondary-subtle" className="text-secondary">
                  Not applicable
                </Badge>
              ) : isRequired ? (
                <Badge color="danger-subtle" className="text-danger">
                  Required
                </Badge>
              ) : (
                <Badge color="light" className="text-muted">
                  Optional
                </Badge>
              )}
              {step.LinkUrl ? (
                <a
                  href={step.LinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-soft-primary py-0"
                >
                  <i className="ri-external-link-line align-bottom me-1" />
                  {step.LinkLabel || "Open settings"}
                </a>
              ) : null}
            </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-1">
          {!excluded && !step.AssignedStaffId && CURRENT_USER_ID ? (
            <Button
              color="soft-primary"
              size="sm"
              title="Assign this step to me"
              onClick={() => onAssigneeChange(step, CURRENT_USER_ID)}
            >
              <i className="ri-user-add-line align-bottom me-1" />
              Assign to me
            </Button>
          ) : null}
          {excluded ? (
            <Button
              color="soft-secondary"
              size="sm"
              title="Restore — count this step again"
              onClick={() => onToggleExcluded(step)}
            >
              <i className="ri-eye-line align-bottom me-1" />
              Restore
            </Button>
          ) : !resolved ? (
            <Button
              color="soft-secondary"
              size="sm"
              title="Mark not applicable — exclude from progress"
              onClick={() => onToggleExcluded(step)}
            >
              <i className="ri-eye-off-line align-bottom me-1" />
              Not applicable
            </Button>
          ) : null}
          {!excluded && !isComplete && completedStatus ? (
            <Button
              color="soft-success"
              size="sm"
              disabled={busy}
              title="Mark this step complete and stamp the completed date/time"
              onClick={() => onStatusChange(step, Number(completedStatus.Id))}
            >
              <i className="ri-check-line align-bottom me-1" />
              Mark complete
            </Button>
          ) : null}
          {!excluded ? (
            <StatusPillSelect
              step={step}
              statusOptions={statusOptions}
              statusIdBySlug={statusIdBySlug}
              busy={busy}
              onChange={onStatusChange}
            />
          ) : null}

          {/* Structural actions: move / edit / delete / not-applicable */}
          <UncontrolledDropdown>
            <DropdownToggle color="light" size="sm" title="More">
              <i className="ri-more-2-fill align-bottom" />
            </DropdownToggle>
            <DropdownMenu end>
              {otherGroups.length > 0 ? (
                <>
                  <DropdownItem
                    onClick={() => {
                      setMoveTarget("");
                      setMoveOpen(true);
                    }}
                  >
                    <i className="ri-folder-transfer-line align-bottom me-2" />
                    Move to group…
                  </DropdownItem>
                  <DropdownItem divider />
                </>
              ) : null}
              {onEditStructure ? (
                <DropdownItem onClick={() => onEditStructure(step)}>
                  <i className="ri-pencil-line align-bottom me-2" />
                  Edit step
                </DropdownItem>
              ) : null}
              {onToggleRequired ? (
                <DropdownItem onClick={() => onToggleRequired(step)}>
                  <i
                    className={`align-bottom me-2 ${
                      isRequired ? "ri-flag-2-line" : "ri-flag-2-fill"
                    }`}
                  />
                  {isRequired ? "Make optional" : "Make required"}
                </DropdownItem>
              ) : null}
              {onDeleteStructure ? (
                <>
                  <DropdownItem divider />
                  <DropdownItem
                    className="text-danger"
                    onClick={() => onDeleteStructure(step)}
                  >
                    <i className="ri-delete-bin-line align-bottom me-2" />
                    Delete step
                  </DropdownItem>
                </>
              ) : null}
            </DropdownMenu>
          </UncontrolledDropdown>

          {/* Move-to-group picker modal */}
          <AppModal
            open={moveOpen}
            onClose={() => setMoveOpen(false)}
            title="Move to group"
            size="sm"
            onSubmit={() => {
              if (moveTarget) onMove(step, moveTarget);
              setMoveOpen(false);
            }}
            submitLabel="Move"
            submitDisabled={!moveTarget}
          >
            <label className="form-label mb-1">Destination group</label>
            <Input
              type="select"
              autoFocus
              value={moveTarget}
              onChange={(e) => setMoveTarget(e.target.value)}
            >
              <option value="">Choose a group…</option>
              {otherGroups.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.name}
                </option>
              ))}
            </Input>
          </AppModal>
        </div>
      </div>

      {/* Always-visible: assignee + due date (both editable without expanding) */}
      <div className="d-flex align-items-center gap-2 flex-wrap mt-2 ms-4 ps-2">
        <Select
          size="small"
          displayEmpty
          value={step.AssignedStaffId ? String(step.AssignedStaffId) : ""}
          onChange={(e) => onAssigneeChange(step, e.target.value)}
          sx={{ minWidth: 180 }}
          renderValue={(val) =>
            val ? (
              <span className="d-inline-flex align-items-center gap-2">
                <StaffAvatar name={assigned?.FullName} size={20} />
                {assigned?.FullName || "—"}
              </span>
            ) : (
              <span className="text-muted">Unassigned</span>
            )
          }
        >
          <MenuItem value="">
            <span className="text-muted">Unassigned</span>
          </MenuItem>
          {staffOptions.map((s) => (
            <MenuItem key={s.Id} value={String(s.Id)}>
              <span className="d-inline-flex align-items-center gap-2">
                <StaffAvatar name={s.FullName} />
                {s.FullName}
              </span>
            </MenuItem>
          ))}
        </Select>
        <Input
          type="date"
          bsSize="sm"
          style={{ maxWidth: 150 }}
          className={isOverdueStep(step, isResolved) ? "border-danger" : ""}
          value={(step.DueDate || "").slice(0, 10)}
          onChange={(e) => onStepDueDateChange(step, e.target.value)}
        />
        {resolved && step.CompletedDateTime ? (
          <span className="text-success small d-inline-flex align-items-center gap-1">
            <i className="ri-checkbox-circle-line" />
            {formatDateTime(step.CompletedDateTime)}
          </span>
        ) : null}
        {noteCount > 0 ? (
          <span className="text-muted small d-inline-flex align-items-center gap-1">
            <i className="ri-chat-1-line" />
            {noteCount}
          </span>
        ) : null}
      </div>

      <Collapse in={open} unmountOnExit>
      {step.Description ? (
        <div className="text-muted small mt-2">{step.Description}</div>
      ) : null}

      {/* Follow-up detail: who owns it + when it's due */}
      {waiting ? (
        <Row className="g-2 mt-2 align-items-end">
          <Col xs={6} sm="auto">
            <label className="form-label mb-1 small text-muted">
              Waiting on
            </label>
            <Select
              size="small"
              fullWidth
              value={step.FollowUpOwner || "customer"}
              onChange={(e) => onOwnerChange(step, e.target.value)}
              sx={{ minWidth: 130 }}
            >
              <MenuItem value="customer">Customer</MenuItem>
              <MenuItem value="us">Us</MenuItem>
            </Select>
          </Col>
          <Col xs={6} sm="auto">
            <label className="form-label mb-1 small text-muted">Due</label>
            <Input
              type="date"
              bsSize="sm"
              value={(step.FollowUpDueDate || "").slice(0, 10)}
              onChange={(e) => onDueChange(step, e.target.value)}
            />
          </Col>
        </Row>
      ) : null}

      {/* Completed date (editable) + who completed it */}
      <Row className="g-3 mt-1 align-items-end">
        <Col xs={12} md={5}>
          <label className="form-label mb-1 small text-muted d-flex align-items-center gap-1">
            Assigned to
            {assigned ? <StaffArchivedBadge staff={assigned} /> : null}
          </label>
          <div className="text-muted small">
            {assigned ? (
              <span className="d-inline-flex align-items-center gap-2">
                <StaffAvatar name={assigned.FullName} size={20} />
                {assigned.FullName}
              </span>
            ) : (
              "Unassigned (set it above)"
            )}
          </div>
        </Col>
        <Col xs={12} md={5}>
          <label className="form-label mb-1 small text-muted">Completed</label>
          <Input
            type="datetime-local"
            bsSize="sm"
            value={toDateTimeLocal(step.CompletedDateTime)}
            onChange={(e) => onCompletedDateChange(step, e.target.value)}
          />
          {step.CompletedStaff_FullName && step.CompletedDateTime ? (
            <div className="text-muted mt-1" style={{ fontSize: 11 }}>
              by {step.CompletedStaff_FullName}
            </div>
          ) : null}
        </Col>
      </Row>

      {/* Captured answer */}
      <div className="mt-3">
        <label className="form-label mb-1 small text-muted">
          Captured value
        </label>
        <Input
          type="text"
          bsSize="sm"
          placeholder="e.g. VAT GB123456789, year end 31 March…"
          value={draftValue(step, "CapturedValue")}
          onChange={(e) => setDraft(step.Id, "CapturedValue", e.target.value)}
          onBlur={() => onFieldSave(step, "CapturedValue")}
        />
      </div>

      {/* Note history */}
      <NotesThread
        step={step}
        notes={notes}
        onAddNote={onAddNote}
        onDeleteNote={onDeleteNote}
      />
      </Collapse>
    </div>
  );
}
